// src/pages/Groups/GroupsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getGroupsList,
  deleteGroup,
  activateGroup,
  updateOrderGroup,
} from "./../../../services/groupServices";
import Button from "../../../components/Button/Button";
import CreateGroup from "./CreateGroup";
import EditGroupModal from "./EditGroupModal";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { useNotification } from "../../../components/Notification/NotificationManager";

import {
  FaPlus,
  FaSearch,
  FaLayerGroup,
  FaCheckCircle,
  FaTimesCircle,
  FaUsers,
  FaImage,
  FaPen,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaSyncAlt,
} from "react-icons/fa";
import { TbTriangleFilled, TbTriangleInvertedFilled } from "react-icons/tb";

/* ================= Helpers ================= */
const PAGE_SIZE = 20;

const STATUS_FILTERS = [
  { value: "all", label: "Tous les groupes" },
  { value: "active", label: "Actifs uniquement" },
  { value: "inactive", label: "Inactifs uniquement" },
];

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return v;
};

const applyFiltersAndSort = (groups, term, status) => {
  let list = Array.isArray(groups) ? [...groups] : [];
  const q = normalize(term);

  if (q) {
    list = list.filter((g) =>
      normalize([g.nom, g.description].filter(Boolean).join(" ")).includes(q)
    );
  }

  if (status === "active") {
    list = list.filter((g) => g.is_active);
  } else if (status === "inactive") {
    list = list.filter((g) => !g.is_active);
  }

  list.sort((a, b) => (a.ordre_groupe ?? 0) - (b.ordre_groupe ?? 0));
  return list;
};

const isCssColor = (value) => {
  if (!value) return false;
  return (
    /^#([0-9A-F]{3}){1,2}$/i.test(value) ||
    /^rgb/.test(value) ||
    /^hsl/.test(value)
  );
};

const getInitials = (name) => {
  const words = (name || "").trim().split(" ").filter(Boolean);
  if (!words.length) return "?";
  if (words.length === 1) return words[0][0]?.toUpperCase() || "?";
  return `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase();
};

const short = (s = "", n = 120) => {
  const t = String(s || "").trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n)}…`;
};

/* ================= UI atoms ================= */
function CompactStat({ icon, label, value, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    rose:
      "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] opacity-75">
            {label}
          </div>
          <div className="mt-0.5 text-xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPageHeader({ totalGroups, activeCount, inactiveCount, totalActors }) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Administration
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
              Gestion des groupes
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              Organisez les groupes d’acteurs, pilotez leur ordre d’affichage et
              leur statut dans l’application de la Ligue.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:min-w-[820px]">
            <CompactStat
              icon={<FaLayerGroup />}
              label="Groupes"
              value={totalGroups}
              accent="indigo"
            />
            <CompactStat
              icon={<FaCheckCircle />}
              label="Actifs"
              value={activeCount}
              accent="emerald"
            />
            <CompactStat
              icon={<FaTimesCircle />}
              label="Inactifs"
              value={inactiveCount}
              accent="rose"
            />
            <CompactStat
              icon={<FaUsers />}
              label="Acteurs"
              value={totalActors}
              accent="amber"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FiltersBar({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  onReset,
  onRefresh,
  onCreate,
  loading,
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr),240px,auto]">
        <div className="relative">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un groupe par nom ou description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Réinitialiser
          </button>

          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaSyncAlt />
            Rafraîchir
          </button>

          <Button
            text="Ajouter un groupe"
            onClick={onCreate}
            icon={FaPlus}
            className="!h-11 !rounded-2xl !px-4"
          />
        </div>
      </div>
    </div>
  );
}

function GroupBadge({ active }) {
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold border transition ${
        active
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
          : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          active ? "bg-emerald-500" : "bg-rose-500"
        }`}
      />
      {active ? "Actif" : "Inactif"}
    </button>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
        <FaLayerGroup className="text-2xl" />
      </div>

      <h3 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
        Aucun groupe trouvé
      </h3>

      <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600 dark:text-slate-300">
        Aucun groupe ne correspond à vos filtres actuels. Vous pouvez modifier
        la recherche, changer le statut affiché ou créer un nouveau groupe.
      </p>

      <div className="mt-5">
        <Button
          text="Créer un groupe"
          onClick={onCreate}
          icon={FaPlus}
          className="!rounded-2xl !px-5 !py-2.5"
        />
      </div>
    </div>
  );
}

function GroupAvatar({ group }) {
  const canUseInlineColor = isCssColor(group.color_groupe);

  return (
    <div
      className={`flex h-12 w-12 items-center justify-center rounded-2xl text-sm font-extrabold shadow-sm ${
        !canUseInlineColor
          ? "bg-gradient-to-br from-blue-500 to-violet-600 text-white"
          : "text-white"
      }`}
      style={canUseInlineColor ? { backgroundColor: group.color_groupe } : undefined}
    >
      {getInitials(group.nom)}
    </div>
  );
}

function GroupImage({ src, alt }) {
  const [imgSrc, setImgSrc] = useState(src || "/img/no-image.jpg");

  useEffect(() => {
    setImgSrc(src || "/img/no-image.jpg");
  }, [src]);

  return (
    <img
      src={imgSrc || "/img/no-image.jpg"}
      alt={alt}
      onError={() => setImgSrc("/img/no-image.jpg")}
      className="h-16 w-24 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700 sm:h-20 sm:w-32"
    />
  );
}

function OrderControl({
  group,
  index,
  total,
  isOrdering,
  onMoveUp,
  onMoveDown,
}) {
  return (
    <div className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 dark:border-slate-700 dark:bg-slate-800">
      <span className="min-w-[20px] text-center text-sm font-bold text-slate-900 dark:text-white">
        {group.ordre_groupe ?? 0}
      </span>

      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => onMoveUp(group.id)}
          disabled={index === 0 || isOrdering}
          className={`transition ${
            index === 0 || isOrdering
              ? "cursor-not-allowed text-slate-300"
              : "text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200"
          }`}
          title="Monter"
        >
          <TbTriangleFilled />
        </button>

        <button
          type="button"
          onClick={() => onMoveDown(group.id)}
          disabled={index === total - 1 || isOrdering}
          className={`transition ${
            index === total - 1 || isOrdering
              ? "cursor-not-allowed text-slate-300"
              : "text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200"
          }`}
          title="Descendre"
        >
          <TbTriangleInvertedFilled />
        </button>
      </div>
    </div>
  );
}

function DesktopTable({
  rows,
  loading,
  onToggleActive,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  indexById,
  totalFiltered,
  isOrdering,
  onCreate,
}) {
  return (
    <div className="hidden lg:block rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      {loading ? (
        <div className="px-4 py-10 text-center text-slate-500">Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="p-6">
          <EmptyState onCreate={onCreate} />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-3xl">
          <table className="w-full min-w-[1200px] table-fixed text-sm">
            <thead className="bg-slate-50 text-left dark:bg-slate-950/60">
              <tr className="text-slate-600 dark:text-slate-300">
                <th className="w-[28%] px-6 py-3 font-semibold">Groupe</th>
                <th className="w-[18%] px-6 py-3 font-semibold">Aperçu</th>
                <th className="w-[14%] px-6 py-3 font-semibold">Ordre</th>
                <th className="w-[12%] px-6 py-3 font-semibold">Statut</th>
                <th className="w-[10%] px-6 py-3 font-semibold">Acteurs</th>
                <th className="w-[18%] px-6 py-3 font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {rows.map((group) => {
                const index = indexById[group.id] ?? 0;

                return (
                  <tr
                    key={group.id}
                    className="align-top transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <GroupAvatar group={group} />

                        <div className="min-w-0">
                          <div className="text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
                            {group.nom || "Sans nom"}
                          </div>

                          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                            ID #{group.id}
                          </div>

                          {group.description ? (
                            <div className="mt-1 line-clamp-2 text-[12px] text-slate-600 dark:text-slate-300">
                              {group.description}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <GroupImage src={group.photo_url} alt={group.nom} />

                        {/* <button
                          type="button"
                          onClick={() => onEdit(group)}
                          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <FaPen />
                          Modifier
                        </button> */}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <OrderControl
                        group={group}
                        index={index}
                        total={totalFiltered}
                        isOrdering={isOrdering}
                        onMoveUp={onMoveUp}
                        onMoveDown={onMoveDown}
                      />
                    </td>

                    <td className="px-6 py-4">
                      <button type="button" onClick={() => onToggleActive(group.id)}>
                        <GroupBadge active={group.is_active} />
                      </button>
                    </td>

                    <td className="px-6 py-4">
                      <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                        <FaUsers className="text-slate-500 dark:text-slate-400" />
                        {group.nombreActeurs || 0}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          title="Éditer"
                          onClick={() => onEdit(group)}
                          className="inline-flex h-10 items-center gap-2 rounded-2xl bg-indigo-600 px-4 text-sm font-semibold text-white transition hover:bg-indigo-700"
                        >
                          <FaPen />
                          {/* Éditer */}
                        </button>

                        <button
                          type="button"
                          title="Supprimer"
                          onClick={() => onDelete(group)}
                          className="inline-flex h-10 items-center gap-2 rounded-2xl bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700"
                        >
                          <FaTrash />
                          {/* Supprimer */}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MobileCards({
  rows,
  loading,
  onToggleActive,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  indexById,
  totalFiltered,
  isOrdering,
  onCreate,
}) {
  return (
    <div className="lg:hidden space-y-3">
      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Chargement…
        </div>
      ) : rows.length === 0 ? (
        <EmptyState onCreate={onCreate} />
      ) : (
        rows.map((group) => {
          const index = indexById[group.id] ?? 0;

          return (
            <div
              key={group.id}
              className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-start gap-3">
                <GroupAvatar group={group} />

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">
                        {group.nom || "Sans nom"}
                      </h3>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        ID #{group.id}
                      </p>
                    </div>

                    <button type="button" onClick={() => onToggleActive(group.id)}>
                      <GroupBadge active={group.is_active} />
                    </button>
                  </div>

                  {group.description ? (
                    <p className="mt-2 line-clamp-2 text-sm text-slate-600 dark:text-slate-300">
                      {group.description}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Ordre
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-black text-slate-900 dark:text-white">
                      {group.ordre_groupe ?? 0}
                    </span>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => onMoveUp(group.id)}
                        disabled={index === 0 || isOrdering}
                        className={`flex h-8 w-8 items-center justify-center rounded-xl ${
                          index === 0 || isOrdering
                            ? "cursor-not-allowed bg-slate-100 text-slate-300 dark:bg-slate-900"
                            : "border border-slate-200 bg-white text-blue-600 dark:border-slate-700 dark:bg-slate-950 dark:text-blue-300"
                        }`}
                      >
                        <TbTriangleFilled />
                      </button>

                      <button
                        type="button"
                        onClick={() => onMoveDown(group.id)}
                        disabled={index === totalFiltered - 1 || isOrdering}
                        className={`flex h-8 w-8 items-center justify-center rounded-xl ${
                          index === totalFiltered - 1 || isOrdering
                            ? "cursor-not-allowed bg-slate-100 text-slate-300 dark:bg-slate-900"
                            : "border border-slate-200 bg-white text-blue-600 dark:border-slate-700 dark:bg-slate-950 dark:text-blue-300"
                        }`}
                      >
                        <TbTriangleInvertedFilled />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Acteurs
                  </div>

                  <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm font-bold text-slate-900 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-white dark:ring-slate-700">
                    <FaUsers className="text-slate-500 dark:text-slate-400" />
                    {group.nombreActeurs || 0}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <GroupImage src={group.photo_url} alt={group.nom} />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  title="Éditer"
                  onClick={() => onEdit(group)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-3 text-sm font-semibold text-white transition hover:bg-indigo-700"
                >
                  <FaPen />
                  {/* Éditer */}
                </button>

                <button
                  type="button"
                  title="Supprimer"
                  onClick={() => onDelete(group)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 text-sm font-semibold text-white transition hover:bg-red-700"
                >
                  <FaTrash />
                  {/* Supprimer */}
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function PaginationBar({ page, pageCount, setPage, loading }) {
  if (pageCount <= 1) return null;

  return (
    <div className="mt-5 flex flex-col items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-4 sm:flex-row dark:border-slate-800 dark:bg-slate-900">
      <div className="text-sm text-slate-500 dark:text-slate-400">
        Page <span className="font-semibold text-slate-900 dark:text-white">{page}</span>{" "}
        sur{" "}
        <span className="font-semibold text-slate-900 dark:text-white">
          {pageCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1 || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaChevronLeft />
          Précédent
        </button>

        <button
          type="button"
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={page === pageCount || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Suivant
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}

/* ================= Page ================= */
const GroupsPage = () => {
  useScrollToTop();
  const { addNotification } = useNotification();

  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 300);
  const [statusFilter, setStatusFilter] = useState("all");

  const [page, setPage] = useState(1);
  const [isOrdering, setIsOrdering] = useState(false);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const data = await getGroupsList();
      setAllGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des groupes", error);
      addNotification("Erreur lors de la récupération des groupes.", "error");
      setAllGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const filteredGroups = useMemo(
    () => applyFiltersAndSort(allGroups, debouncedSearch, statusFilter),
    [allGroups, debouncedSearch, statusFilter]
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  const totalGroups = allGroups.length;
  const activeCount = allGroups.filter((g) => g.is_active).length;
  const inactiveCount = totalGroups - activeCount;
  const totalActors = allGroups.reduce(
    (acc, g) => acc + Number(g.nombreActeurs || 0),
    0
  );

  const pageCount = Math.max(1, Math.ceil(filteredGroups.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  const paginatedGroups = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredGroups.slice(start, start + PAGE_SIZE);
  }, [filteredGroups, safePage]);

  const indexById = useMemo(() => {
    return filteredGroups.reduce((acc, g, idx) => {
      acc[g.id] = idx;
      return acc;
    }, {});
  }, [filteredGroups]);

  const handleDeleteGroup = async (group) => {
    if (group.nombreActeurs > 0) {
      addNotification(
        "Impossible de supprimer un groupe avec des membres actifs.",
        "error"
      );
      return;
    }

    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer le groupe "${group.nom}" ?`
      )
    ) {
      return;
    }

    try {
      await deleteGroup(group.id);
      addNotification("Groupe supprimé avec succès.", "success");
      setAllGroups((prev) => prev.filter((g) => g.id !== group.id));
    } catch (error) {
      console.error("Erreur lors de la suppression d’un groupe", error);
      addNotification("Erreur lors de la suppression d’un groupe.", "error");
    }
  };

  const toggleActiveStatus = async (groupId) => {
    const previous = [...allGroups];
    const updated = allGroups.map((g) =>
      g.id === groupId ? { ...g, is_active: !g.is_active } : g
    );

    setAllGroups(updated);

    try {
      await activateGroup(groupId);
      addNotification("Statut du groupe mis à jour avec succès.", "success");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut du groupe", error);
      addNotification(
        "Erreur lors de la mise à jour du statut du groupe.",
        "error"
      );
      setAllGroups(previous);
    }
  };

  const openEditModal = (group) => {
    setSelectedGroup(group);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedGroup(null);
    setIsEditModalOpen(false);
  };

  const moveGroup = async (groupId, direction) => {
    if (isOrdering) return;
    setIsOrdering(true);

    const prevList = [...allGroups];

    try {
      const currentFiltered = applyFiltersAndSort(
        allGroups,
        debouncedSearch,
        statusFilter
      );

      const idx = currentFiltered.findIndex((g) => g.id === groupId);
      if (idx === -1) return;

      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= currentFiltered.length) return;

      const currentId = currentFiltered[idx].id;
      const targetId = currentFiltered[targetIdx].id;

      const list = [...allGroups];
      const i = list.findIndex((g) => g.id === currentId);
      const j = list.findIndex((g) => g.id === targetId);
      if (i === -1 || j === -1) return;

      const tmp = list[i].ordre_groupe ?? 0;
      list[i].ordre_groupe = list[j].ordre_groupe ?? tmp;
      list[j].ordre_groupe = tmp;

      list.sort((a, b) => (a.ordre_groupe ?? 0) - (b.ordre_groupe ?? 0));
      setAllGroups(list);

      await updateOrderGroup(list);
      addNotification("Ordre des groupes mis à jour.", "success");
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'ordre des groupes", error);
      addNotification(
        "Erreur lors de la mise à jour de l'ordre des groupes.",
        "error"
      );
      setAllGroups(prevList);
    } finally {
      setIsOrdering(false);
    }
  };

  const moveGroupUp = (groupId) => moveGroup(groupId, "up");
  const moveGroupDown = (groupId) => moveGroup(groupId, "down");

  const handleReset = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setPage(1);
  };

  return (
    <div className="w-full pb-10 pt-[96px] font-satoshi">
      <div className="mx-auto max-w-[1400px] px-4">
        <AdminPageHeader
          totalGroups={totalGroups}
          activeCount={activeCount}
          inactiveCount={inactiveCount}
          totalActors={totalActors}
        />

        <div className="mt-5">
          <FiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            onReset={handleReset}
            onRefresh={fetchGroups}
            onCreate={() => setIsCreateModalOpen(true)}
            loading={loading}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {filteredGroups.length} groupe{filteredGroups.length > 1 ? "s" : ""}
          </span>

          {statusFilter !== "all" && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Filtre :{" "}
              {STATUS_FILTERS.find((f) => f.value === statusFilter)?.label}
            </span>
          )}

          {debouncedSearch && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Recherche : {searchTerm}
            </span>
          )}

          {!loading && filteredGroups.length > 0 && (
            <span className="ml-auto text-xs">
              Affichage {paginatedGroups.length} • page {safePage} / {pageCount}
            </span>
          )}
        </div>

        <div className="mt-5">
          <DesktopTable
            rows={paginatedGroups}
            loading={loading}
            onToggleActive={toggleActiveStatus}
            onEdit={openEditModal}
            onDelete={handleDeleteGroup}
            onMoveUp={moveGroupUp}
            onMoveDown={moveGroupDown}
            indexById={indexById}
            totalFiltered={filteredGroups.length}
            isOrdering={isOrdering}
            onCreate={() => setIsCreateModalOpen(true)}
          />

          <MobileCards
            rows={paginatedGroups}
            loading={loading}
            onToggleActive={toggleActiveStatus}
            onEdit={openEditModal}
            onDelete={handleDeleteGroup}
            onMoveUp={moveGroupUp}
            onMoveDown={moveGroupDown}
            indexById={indexById}
            totalFiltered={filteredGroups.length}
            isOrdering={isOrdering}
            onCreate={() => setIsCreateModalOpen(true)}
          />
        </div>

        <PaginationBar
          page={safePage}
          pageCount={pageCount}
          setPage={setPage}
          loading={loading}
        />
      </div>

      <CreateGroup
        isActive={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        refreshGroups={fetchGroups}
      />

      {isEditModalOpen && (
        <EditGroupModal
          isActive={isEditModalOpen}
          onClose={closeEditModal}
          refreshGroups={fetchGroups}
          groupData={selectedGroup}
        />
      )}
    </div>
  );
};

export default GroupsPage;